import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import IPButton from '../../../../../components/IPButton'
import IPInput from '../../../../../components/IPInput'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import IPExcelButton from '../../../../../components/IPExcelButton'
import IPModal from '../../../../../components/IPModal'
import IPRadio from '../../../../../components/IPRadio'
import IPCheckbox from '../../../../../components/IPCheckBox'
import InputCampaign from '../../../../../components/InputCampaign'
import Table from '../../../../../components/Table'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { exportToXLSX } from '../../../../../base/utils/dataHelper'
import {
  DealerContractRejectScope,
  DealerContractStatus,
  DealerType,
  IDealerContractDetail,
  IDealerContractFilter,
  IDealerContractListItem,
} from '../models/dealerContractAdminModels'
import {
  ApproveDealerContractApi,
  GetDealerContractDetailApi,
  GetDealerContractListApi,
  RejectDealerContractApi,
} from '../services/dealerContractAdminApi'

const statusOptions: ISelectOption[] = [
  { label: 'Beklemede', value: DealerContractStatus.Beklemede },
  { label: 'İnceleme Bekliyor', value: DealerContractStatus.IncelemeBekliyor },
  { label: 'Onaylandı', value: DealerContractStatus.Onaylandi },
  { label: 'Reddedildi', value: DealerContractStatus.Reddedildi },
  { label: 'Düzeltme - İnceleme Bekliyor', value: DealerContractStatus.DuzeltmeIncelemeBekliyor },
]

const dealerTypeOptions: ISelectOption[] = [
  { label: 'Tümü', value: DealerType.All },
  { label: 'Normal', value: DealerType.Normal },
  { label: 'Vodafone', value: DealerType.Vodafone },
]

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '-'
  return d.toLocaleString('tr-TR')
}

const statusBadgeClass = (status: DealerContractStatus) => {
  switch (status) {
    case DealerContractStatus.Onaylandi:
      return 'badge bg-success'
    case DealerContractStatus.Reddedildi:
      return 'badge bg-danger'
    case DealerContractStatus.IncelemeBekliyor:
    case DealerContractStatus.DuzeltmeIncelemeBekliyor:
      return 'badge bg-warning text-dark'
    default:
      return 'badge bg-secondary'
  }
}

/**
 * Bayi Sözleşme ve Belge Onayları.
 *
 * Belgeler manuel kontrol edilir. Red kapsamı bayinin ne yapacağını belirler:
 * Belge -> yalnız belge yeniden yüklenir, sözleşme onayı korunur.
 * Bilgi / Belge+Bilgi -> bilgi düzeltilir ve sözleşme yeniden onaylatılır.
 * Reddedilen bayi, düzeltmesi onaylanana kadar siteyi kullanamaz.
 */
function DealerContractApproval(props: BaseComponentWithContextProps) {
  const [filter, setFilter] = useState<IDealerContractFilter>({})
  const [statusOption, setStatusOption] = useState<ISelectOption | undefined>(undefined)
  const [list, setList] = useState<IDealerContractListItem[]>([])

  const [detail, setDetail] = useState<IDealerContractDetail | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  const [showReject, setShowReject] = useState(false)
  const [rejectId, setRejectId] = useState<number | null>(null)
  const [rejectScope, setRejectScope] = useState<DealerContractRejectScope>(DealerContractRejectScope.Belge)
  const [rejectReason, setRejectReason] = useState('')

  const getList = useCallback(async () => {
    props.showLoading()
    try {
      const api = new GetDealerContractListApi({})
      const res = await api.getList(filter)
      setList(res ?? [])
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description ?? 'Liste alınamadı.',
        showConfirmButton: true,
      })
    } finally {
      props.hideLoading()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  useEffect(() => {
    getList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openDetail = async (id: number) => {
    props.showLoading()
    try {
      const api = new GetDealerContractDetailApi({})
      const res = await api.getDetail(id)
      setDetail(res)
      setShowDetail(true)
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description ?? 'Detay alınamadı.',
        showConfirmButton: true,
      })
    } finally {
      props.hideLoading()
    }
  }

  const approve = async (item: IDealerContractListItem) => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Onaylıyor musunuz?',
      html: `<b>${item.DealerSapCode}</b> - ${item.CompanyName ?? ''}<br/>Bayi sözleşme ve belgelerini onayladığınızda bayi siteyi kullanmaya devam eder.`,
      showCancelButton: true,
      confirmButtonText: 'Onayla',
      cancelButtonText: 'Vazgeç',
    })
    if (!confirm.isConfirmed) return

    props.showLoading()
    try {
      const api = new ApproveDealerContractApi(item.Id, {})
      await api.approve()
      Swal.fire({ icon: 'success', title: 'Onaylandı', showConfirmButton: true })
      setShowDetail(false)
      await getList()
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description ?? 'Onaylanamadı.',
        showConfirmButton: true,
      })
    } finally {
      props.hideLoading()
    }
  }

  const openReject = (id: number) => {
    setRejectId(id)
    setRejectScope(DealerContractRejectScope.Belge)
    setRejectReason('')
    setShowReject(true)
  }

  const submitReject = async () => {
    if (!rejectId) return

    const reason = rejectReason.trim()
    if (reason.length < 5) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Red gerekçesi yazılmalıdır. Bu gerekçe bayiye gösterilecektir.',
        showConfirmButton: true,
      })
      return
    }

    props.showLoading()
    try {
      const api = new RejectDealerContractApi(rejectId, {})
      await api.reject({ Scope: rejectScope, Reason: reason })
      setShowReject(false)
      setShowDetail(false)
      Swal.fire({
        icon: 'success',
        title: 'Reddedildi',
        text: 'Bayiye red gerekçesi gösterilecek ve düzeltme onaylanana kadar siteyi kullanamayacak.',
        showConfirmButton: true,
      })
      await getList()
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description ?? 'Reddedilemedi.',
        showConfirmButton: true,
      })
    } finally {
      props.hideLoading()
    }
  }

  /**
   * Ekranda filtreli duran listeyi Excel'e aktarir. Ayri bir servis yok; export
   * her zaman o an listelenen kayitlari yazar, sayfalamadan bagimsizdir.
   */
  const exportExcel = () => {
    if (list.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Kayıt yok',
        text: 'Aktarılacak kayıt bulunmuyor. Lütfen önce filtreleyip liste getiriniz.',
        showConfirmButton: true,
      })
      return
    }

    const rows = list.map(item => ({
      'Bayi Kodu': item.DealerSapCode,
      Ünvan: item.CompanyName,
      'Sözleşme Onay Tarihi': item.IsContractConfirmation ? formatDate(item.ContractConfirmDate) : 'Onaylanmadı',
      Durum: item.StatusText,
      'Red Nedeni': item.RejectReason ?? '',
    }))

    const stamp = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')
    exportToXLSX(rows, `Bayi-Sozlesme-Onaylari-${stamp}`)
  }

  // Paylasilan Table bileseni (react-table) sayfalama + siralama getiriyor.
  const columnDefinitions = [
    { Header: '#', accessor: 'index' },
    { Header: 'Bayi Kodu', accessor: 'dealerCode' },
    { Header: 'Ünvan', accessor: 'companyName' },
    { Header: 'Yetkili', accessor: 'authorized' },
    { Header: 'Telefon', accessor: 'phone' },
    { Header: 'Durum', accessor: 'status' },
    { Header: 'Sözleşme Onayı', accessor: 'contractConfirm' },
    { Header: 'Belge', accessor: 'documents' },
    { Header: 'Son Hareket', accessor: 'lastAction' },
    { Header: 'İşlem', accessor: 'actions' },
  ]

  const tableColumns = useMemo(() => columnDefinitions, [])

  const tableData = useMemo(() => {
    return list.map((item, index) => ({
      index: index + 1,
      dealerCode: item.DealerSapCode,
      companyName: item.CompanyName,
      authorized: item.AuthorizedNameSurname,
      phone: item.MobilePhone,
      status: (
        <>
          <span className={statusBadgeClass(item.Status)}>{item.StatusText}</span>
          {item.IsDealerEditing && (
            <div>
              <span className='badge bg-info text-dark' style={{ marginTop: 4 }}>
                Bayi düzenliyor
              </span>
              <div style={{ fontSize: '0.75rem', color: '#666' }}>{formatDate(item.DealerEditingWhen)}</div>
            </div>
          )}
          {item.Status === DealerContractStatus.Reddedildi && item.RejectReason ? (
            <div style={{ fontSize: '0.8rem', color: '#c00', marginTop: 4 }}>{item.RejectReason}</div>
          ) : null}
        </>
      ),
      contractConfirm: item.IsContractConfirmation ? formatDate(item.ContractConfirmDate) : 'Onaylanmadı',
      documents: (
        <>
          {item.DocumentCount} adet
          <div style={{ fontSize: '0.8rem', color: '#666' }}>{formatDate(item.DocumentsUploadDate)}</div>
        </>
      ),
      lastAction: formatDate(item.LastActionWhen),
      actions: (
        <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
          <IPButton
            type='button'
            className='btn btn-primary'
            style={{ width: 'auto' }}
            text='Detay'
            onClick={() => openDetail(item.Id)}
          />
          <IPButton
            type='button'
            className='btn btn-success'
            style={{ width: 'auto' }}
            text='Onayla'
            disabled={item.IsDealerEditing}
            onClick={() => approve(item)}
          />
          <IPButton
            type='button'
            className='btn btn-danger'
            style={{ width: 'auto' }}
            text='Reddet'
            onClick={() => openReject(item.Id)}
          />
        </div>
      ),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list])

  const detailRow = (label: string, value?: string | null) => (
    <div className='col-lg-4 mb-2' key={label}>
      <div style={{ fontSize: '0.8rem', color: '#666' }}>{label}</div>
      <div>{value && value.length > 0 ? value : '-'}</div>
    </div>
  )

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header'>
            <span className='h4'>Bayi Sözleşme ve Belge Onayları</span>
          </div>
          <div className='card-body'>
            {/* Muafiyet notu: kapsam kuralı backend'de DealerContractService.IsInScope içindedir. */}
            <div className='alert alert-info' role='alert'>
              <b>DealerNonContract</b> alt tipindeki bayiler sözleşme zorunluluğundan <b>muaftır</b>: bu bayilere
              sözleşme popup'ı çıkmaz, ödeme ve siparişleri engellenmez, yeni sözleşme kaydı oluşmaz. Bir bayiyi muaf
              tutmak için cari alt tiplerine <b>DealerNonContract</b> eklenmesi yeterlidir. Alt tip eklenmeden önce
              akışa girmiş bir bayinin kaydı bu listede görünmeye devam eder; ancak erişimi artık engellenmez.
            </div>

            <form className='formbanner' onSubmit={e => e.preventDefault()}>
              <div className='row'>
                <div className='col-lg-3'>
                  <label className='form-label'>Durum</label>
                  <IPSelectBox
                    isClearable
                    id='dcStatus'
                    name='dcStatus'
                    options={statusOptions}
                    value={statusOption}
                    onChangeSingle={option => {
                      setStatusOption(option ?? undefined)
                      setFilter(prev => ({ ...prev, Status: option ? Number(option.value) : undefined }))
                    }}
                  />
                </div>
                <div className='col-lg-2'>
                  <label className='form-label'>Bayi Kodu</label>
                  <IPInput
                    type='text'
                    className='form-control'
                    maxLength={10}
                    value={filter.SapCode ?? ''}
                    onChange={e => setFilter(prev => ({ ...prev, SapCode: e.target.value.replace(/\D/g, '') }))}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>Baştaki sıfırları girmeniz gerekmez.</div>
                </div>
                <div className='col-lg-2'>
                  <label className='form-label'>Cari Türü</label>
                  <IPSelectBox
                    id='dealerType'
                    name='dealerType'
                    placeholder='Cari Türü Seçiniz'
                    options={dealerTypeOptions}
                    onChangeSingle={option =>
                      setFilter(prev => ({ ...prev, DealerType: option.value.toString() }))
                    }
                  />
                </div>
                <div className='col-lg-2'>
                  <label className='form-label'></label>
                  <InputCampaign
                    type='date'
                    id='dcStartDate'
                    name='dcStartDate'
                    label='Başlangıç Tarihi'
                    value={filter.StartDate ?? ''}
                    onChange={e => setFilter(prev => ({ ...prev, StartDate: e.target.value }))}
                    isActive
                  />
                </div>
                <div className='col-lg-2'>
                  <label className='form-label'></label>
                  <InputCampaign
                    type='date'
                    id='dcEndDate'
                    name='dcEndDate'
                    label='Bitiş Tarihi'
                    value={filter.EndDate ?? ''}
                    onChange={e => setFilter(prev => ({ ...prev, EndDate: e.target.value }))}
                    isActive
                  />
                </div>
                <div className='col-lg-2 d-flex align-items-end'>
                  <IPButton type='button' className='btn btn-success' text='Filtrele' onClick={getList} />
                </div>
              </div>
            </form>

            <br />

            <div className='d-flex justify-content-between align-items-center'>
              <b>{list.length} kayıt listeleniyor.</b>
              <IPExcelButton onClick={exportExcel} />
            </div>
            <Table columns={tableColumns} data={tableData} currentPage={0} />
          </div>
        </div>
      </div>

      {/* Detay: bilgiler, belgeler, sözleşme ve hareket geçmişi */}
      <IPModal
        title='Bayi Sözleşme Detayı'
        show={showDetail}
        width={1200}
        hideConfirm
        closeOnEscapeKey
        onClose={() => setShowDetail(false)}
      >
        <>
          {!detail ? (
            <div>Yükleniyor...</div>
          ) : (
            <div style={{ textAlign: 'left' }}>
              <h5>Bayi Bilgileri</h5>
              <div className='row'>
                {detailRow('Bayi Kodu', detail.Dealer?.DealerSapCode)}
                {detailRow('Ünvan', detail.Dealer?.CompanyName)}
                {detailRow('Vergi / TC No', detail.Dealer?.TaxOrTcNumber)}
                {detailRow('Vergi Dairesi', detail.Dealer?.TaxAdministration)}
                {detailRow('E-posta', detail.Dealer?.Email)}
                {detailRow('Telefon', detail.Dealer?.MobilePhone)}
                {detailRow('Adres', detail.Dealer?.Address)}
              </div>

              <hr />
              <h5>Bayinin Girdiği Bilgiler</h5>
              <div className='row'>
                {detailRow('Yetkili Ad Soyad', detail.Info?.AuthorizedNameSurname)}
                {detailRow('Yetkili TC', detail.Info?.AuthorizedTc)}
                {detailRow(
                  'Mersis No',
                  detail.Info?.HasNoMersis && !detail.Info?.MersisNo ? 'Mersis kaydı yok' : detail.Info?.MersisNo
                )}
                {detailRow('E-posta (bayinin girdiği)', detail.Info?.Email)}
                {detailRow('Telefon (bayinin girdiği)', detail.Info?.MobilePhone)}
                {detailRow('Ticari İleti Onayı', detail.Info?.CommercialMessage ? 'Evet' : 'Hayır')}
                {detailRow('KVKK Onayı', detail.Info?.KvkkApproval ? 'Onaylandı' : 'Onaylanmadı')}
                {detailRow('Sözleşme Sürümü', detail.ContractVersion)}
                {detailRow('Onay IP', detail.ContractConfirmIp)}
                {detailRow('Sözleşme Onay Tarihi', formatDate(detail.Header?.ContractConfirmDate))}
              </div>

              <hr />
              <h5>Belgeler</h5>
              {detail.Documents?.length > 0 ? (
                <ul>
                  {detail.Documents.map(doc => (
                    <li key={doc.FileName}>
                      <a href={doc.FileUrl} target='_blank' rel='noreferrer'>
                        {doc.Name}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <div>Belge yüklenmemiş.</div>
              )}
              {detail.ContractFileUrl ? (
                <div>
                  <a href={detail.ContractFileUrl} target='_blank' rel='noreferrer'>
                    Bayilik Sözleşmesi (PDF)
                  </a>
                </div>
              ) : (
                <div>Sözleşme belgesi yok.</div>
              )}

              <hr />
              <h5>Hareket Geçmişi</h5>
              <table className='table table-bordered table-sm'>
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>İşlem</th>
                    <th>Kapsam</th>
                    <th>Gerekçe / Not</th>
                    <th>Değişiklikler</th>
                    <th>Kullanıcı</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.History ?? []).length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center' }}>
                        Hareket kaydı yok.
                      </td>
                    </tr>
                  )}
                  {(detail.History ?? []).map(log => (
                    <tr key={log.Id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.LogWhen)}</td>
                      <td>{log.ActionText}</td>
                      <td>{log.ScopeText ?? '-'}</td>
                      <td>{log.Message ?? '-'}</td>
                      <td>
                        {log.Changes?.length > 0 ? (
                          log.Changes.map(change => (
                            <div key={`${log.Id}-${change.Field}`} style={{ fontSize: '0.85rem' }}>
                              <b>{change.Field}:</b>{' '}
                              <span style={{ color: '#c00' }}>{change.Old || '(boş)'}</span> →{' '}
                              <span style={{ color: '#080' }}>{change.New || '(boş)'}</span>
                            </div>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                        {log.Documents?.length > 0 && (
                          <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                            {log.Documents.map(doc => (
                              <div key={`${log.Id}-${doc.FileName}`}>
                                <a href={doc.FileUrl} target='_blank' rel='noreferrer'>
                                  {doc.Name}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {log.UserName ?? log.UserId ?? '-'}
                        {log.IsAdmin ? ' (admin)' : ''}
                      </td>
                      <td>{log.ClientIp ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {detail.Header?.IsDealerEditing && (
                <div className='alert alert-info'>
                  <b>Bayi şu anda bilgilerini güncelliyor.</b> Sözleşmeyi yeniden oluşturmak için forma
                  döndü ({formatDate(detail.Header.DealerEditingWhen)}). Bayi işlemi tamamlayana kadar
                  bu kayıt onaylanamaz.
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <IPButton
                  type='button'
                  className='btn btn-success'
                  style={{ width: 'auto' }}
                  text='Onayla'
                  disabled={detail.Header?.IsDealerEditing}
                  onClick={() => detail.Header && approve(detail.Header)}
                />
                <IPButton
                  type='button'
                  className='btn btn-danger'
                  style={{ width: 'auto' }}
                  text='Reddet'
                  onClick={() => detail.Header && openReject(detail.Header.Id)}
                />
              </div>
            </div>
          )}
        </>
      </IPModal>

      {/* Red: kapsam bayinin ne yapacağını belirler */}
      <IPModal
        title='Reddet'
        show={showReject}
        width={700}
        confirmText='Reddet'
        onConfirm={submitReject}
        onClose={() => setShowReject(false)}
        closeOnEscapeKey
      >
        <div style={{ textAlign: 'left' }}>
          <label className='form-label'>Red Kapsamı</label>
          <div style={{ marginBottom: 12 }}>
            <IPRadio
              text='Belge — yalnızca belgeler yeniden istenir, sözleşme onayı korunur'
              name='dcRejectScope'
              onClick={() => setRejectScope(DealerContractRejectScope.Belge)}
              checked={rejectScope === DealerContractRejectScope.Belge}
            />
            <IPRadio
              text='Bilgi — bilgiler düzeltilir ve sözleşme yeniden onaylatılır'
              name='dcRejectScope'
              onClick={() => setRejectScope(DealerContractRejectScope.Bilgi)}
              checked={rejectScope === DealerContractRejectScope.Bilgi}
            />
            <IPRadio
              text='Belge ve Bilgi — ikisi de yenilenir ve sözleşme yeniden onaylatılır'
              name='dcRejectScope'
              onClick={() => setRejectScope(DealerContractRejectScope.BelgeVeBilgi)}
              checked={rejectScope === DealerContractRejectScope.BelgeVeBilgi}
            />
          </div>

          <label className='form-label'>Red Gerekçesi (bayiye gösterilir)</label>
          <textarea
            className='form-control'
            rows={4}
            maxLength={1000}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
          />
          <div className='alert alert-warning' style={{ marginTop: 12 }}>
            Reddedilen bayi, düzeltmesi onaylanana kadar siteyi kullanamaz; ödeme ve sipariş işlemleri de
            engellenir.
          </div>
        </div>
      </IPModal>
    </div>
  )
}

export default componentWithContext(DealerContractApproval)
